import {useCallback, useEffect, useRef, useState} from 'react'

import {API_MODES, REFINE_ERROR_CODES} from '../lib/refineApi'
import {heuristicPoints} from '../lib/talkingPoints'

/**
 * The talking points for the story as it reads right now (TK-3204 "Use
 * talking points"). `load()` asks the API's `talking_points` mode once per
 * text and falls back to the prototype's first-words heuristic when the
 * call fails or comes back empty, so the recorder always has something to
 * show. `reset()` forgets a list the writer has moved on from.
 */
const useTalkingPoints = ({getText, question, request}) => {
  const [points, setPoints] = useState(null)
  const [loading, setLoading] = useState(false)
  const controllerRef = useRef(null)
  const forTextRef = useRef(null)

  const abort = useCallback(() => {
    if (controllerRef.current) controllerRef.current.abort()

    controllerRef.current = null
  }, [])

  useEffect(() => abort, [abort])

  const load = useCallback(() => {
    const text = getText()

    if (points && forTextRef.current === text) return
    if (controllerRef.current && forTextRef.current === text) return

    abort()
    forTextRef.current = text
    setPoints(null)

    if (!text.trim()) {
      setLoading(false)

      return
    }

    const controller = new AbortController()

    controllerRef.current = controller
    setLoading(true)

    request({mode: API_MODES.TALKING_POINTS, question, text}, controller.signal)
      .then(result => {
        if (controller.signal.aborted) return

        const list = Array.isArray(result.talkingPoints) ? result.talkingPoints : []

        setPoints(list.length ? list : heuristicPoints(text))
      })
      .catch(error => {
        if (controller.signal.aborted || (error && error.code === REFINE_ERROR_CODES.CANCELLED)) return

        setPoints(heuristicPoints(text))
      })
      .then(() => {
        if (controllerRef.current === controller) {
          controllerRef.current = null
          setLoading(false)
        }
      })
  }, [getText, question, request, points, abort])

  const reset = useCallback(() => {
    abort()
    forTextRef.current = null
    setPoints(null)
    setLoading(false)
  }, [abort])

  return {points, loading, load, reset}
}

export default useTalkingPoints
